import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import { Navigate } from 'react-router-dom';

import OnboardingFlow from '../components/OnboardingFlow';
import OnboardingWizard from '../components/OnboardingWizard';
import { useOnboardingState } from '../hooks/queries/useOnboardingState';
import { useFeatureGates } from '../hooks/useFeatureGates';
import { PATHS } from '../paths';

/**
 * Redirects a fully-onboarded viewer away from `/onboarding`. Without this,
 * `OnboardingFlow` renders `null` for an onboarded user (its `resumeScreen`
 * is `null`), leaving a stable blank page on direct navigation to the route.
 *
 * Holds (`null`) while the resume derivation is in flight — a cache-cold tick
 * reports `needsOnboarding === false` and would bounce a viewer who must
 * still onboard. Mirrors `ObligationsRouteGate` in `MainApp.tsx`.
 */
const OnboardingFlowGate = () => {
  const { needsOnboarding, loading } = useOnboardingState();
  if (loading) {
    return null;
  }
  return needsOnboarding ? <OnboardingFlow /> : <Navigate to={PATHS.DASHBOARD} replace />;
};

/**
 * Route entry for `/onboarding`. Branches on `feat-onboarding-v2`:
 * - On → renders the new {@link OnboardingFlow} (owns its own full-screen layout).
 * - Off → renders the legacy {@link OnboardingWizard} inside the existing brand wrapper.
 *
 * Renders `null` while the gate set is bootstrapping (fail-closed) so the
 * legacy wizard can't flash before the v2 gate resolves for an enabled
 * viewer. Same pattern as `ObligationsRouteGate` / `AssistantRouteGate` in
 * `MainApp.tsx`. The legacy wizard comes out entirely once v2 graduates to
 * `ALL_USERS` and the gate is retired (see ORN-582).
 */
const OnboardingScene = () => {
  const { flags, loading } = useFeatureGates();
  if (loading && flags.size === 0) {
    return null;
  }
  if (flags.has(FeatureGate.OnboardingV2)) {
    return <OnboardingFlowGate />;
  }
  return (
    <div className="bg-brand-solid flex h-full w-full flex-col items-center justify-center">
      <OnboardingWizard />
    </div>
  );
};

export default OnboardingScene;
